<script setup lang="ts">
import { useSlots } from 'vue'

import { useSanitizer } from '@/composables/useSanitizer'

type Component = 'h1' | 'h2' | 'h3' | 'h4' | 'span' | 'p' | 'label'
type Variant = | 'h1' | 'h2' | 'h3' | 'h4' | 'body' | 'preamble' | 'number-small' | 'number-large' | 'caption' | 'label' | 'bullet-label'
type TypographyFontWeight = 200 | 300 | 400 | 500 | 600 | 700 | 800

// Use the component prop if the desired visual style differs from the required semantic html tag.
const props = defineProps<{
  component?: Component
  variant?: Variant
  fontWeight?: TypographyFontWeight
}>()

const componentMap: Record<Variant, Component> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  body: 'p',
  ['preamble']: 'p',
  ['number-small']: 'span',
  ['number-large']: 'span',
  caption: 'span',
  label: 'label',
  ['bullet-label']: 'label',
}

const { sanitize } = useSanitizer()
const variant = props.variant ?? 'body'
const computedComponent = props.component ? props.component : componentMap[variant]
const isTitle = ['h1', 'h2', 'h3', 'h4'].includes(computedComponent ?? '')
const slots = useSlots()
const sanitizedContent = computed(() => {
  const contentFromSlot = slots.default?.({})[0].children as string
  return sanitize(contentFromSlot)
})
</script>

<template>
  <component
    :is="computedComponent"
    :class="`typography typography--${variant.toString()}`"
    :style="{ fontWeight: props.fontWeight }"
  >
    <span
      v-if="isTitle"
      v-html="sanitizedContent"
    />
    <slot v-else />
  </component>
</template>

<style lang="scss" scoped>
  .typography {
    word-break: break-word;

    &--h1 {
      @include h1;
    }

    &--h2 {
      @include h2;
    }

    &--h3 {
      @include h3;
    }

    &--h4 {
      @include h4;
    }

    &--label {
      @include label;
    }

    &--body {
      @include body;
    }

    &--preamble {
      @include preamble;
    }

    &--number-small {
      @include number-small;
    }

    &--number-large {
      @include number-large;
    }

    &--caption {
      @include caption;
    }

    &--bullet-label {
      @include bullet-label;
    }
  }
</style>
